import { useEffect, useRef } from "react"
import { useAppToast } from "@ui-seeker/app"
import { postSubmit } from "@ui-seeker/utils"

import { BACKEND_APIS } from "@constants"
import {
    appliedFilters,
    coverage,
    coverageTotal,
    currentPage,
    filtersValue,
    hasSearched,
    isLoadingFilters,
    isLoadingTable,
    offerSummary,
    pageSize,
    qualityFlags,
    searchText,
    sortOrder,
    tableData,
    totalCount,
} from "../@signals"


export default function useScrapingDataReview() {
    const { showToast } = useAppToast()
    //: Set when a fetch has already been asked for and the page is only being
    //: rewound to 0. Without it, the paging effect fires a second, identical
    //: request for the same state.
    const pageResetHandled = useRef(false)

    // Who else was selling each product on the page, in a second request that
    // the table does not wait for. It is one primary-key lookup in
    // MARKETPLACES for the whole page — 34 ms for 50 products, measured on
    // production 2026-09-22 — and keeping it out of /search is what lets that
    // query stay one table and one index.
    //
    // Failures are swallowed: this decorates the table, it does not carry it.
    const fetchOfferSummary = (rows) => {
        offerSummary.value = {}
        if (!rows.length) return

        postSubmit(`${BACKEND_APIS.SCRAPING_DATA_REVIEW}/offers`, {
            customer: appliedFilters.value?.customer,
            items: rows.map(row => ({
                siteID: row.siteID,
                productID: row.productID,
                date: row.date,
            })),
        })
            .then(res => { if (res) offerSummary.value = res })
            .catch(() => {})
    }

    // Always the applied snapshot, never `filtersValue`: the filter bar's
    // controls only take effect on Search, and reading them here made sorting
    // or paging pick up a site the user had merely selected.
    const fetchPage = () => {
        const applied = appliedFilters.value
        if (applied?.customer == null) return

        hasSearched.value = true
        isLoadingTable.value = true

        postSubmit(`${BACKEND_APIS.SCRAPING_DATA_REVIEW}/search`, {
            ...applied,
            sort: sortOrder.value,
            page: currentPage.value,
            pageSize: pageSize.value,
        })
            .then(res => {
                if (!res) return
                tableData.value = res.data || []
                totalCount.value = res.total || 0
                fetchOfferSummary(res.data || [])
                // The coverage travels with the page (~1 s for the aggregate),
                // so there is nothing to stagger — but it describes `coverageTotal`
                // rows, not the ones on screen.
                coverage.value = res.coverage || null
                coverageTotal.value = res.coverageTotal || 0
            })
            .catch(() => showToast.error('Error loading scraping data'))
            .finally(() => {
                isLoadingTable.value = false
                isLoadingFilters.value = false
            })
    }

    // Rewind to page 0 and fetch once. Anything that changes WHICH rows match
    // has to do both: staying on page 12 of a different result shows an
    // arbitrary slice of it.
    const fetchFromFirstPage = () => {
        if (currentPage.value !== 0) {
            pageResetHandled.current = true
            currentPage.value = 0
        }
        fetchPage()
    }

    // Search: this is the only place the filter bar's controls are read.
    useEffect(() => {
        if (!isLoadingFilters.value) return
        appliedFilters.value = { ...filtersValue.value }
        fetchPage()
    }, [isLoadingFilters.value])

    // Paging re-fetches server-side; it must not re-run before a first search.
    useEffect(() => {
        if (pageResetHandled.current) {
            pageResetHandled.current = false
            return
        }
        if (totalCount.value > 0) fetchPage()
    }, [currentPage.value, pageSize.value])

    // Sorting is server-side too.
    useEffect(() => {
        if (!hasSearched.value) return
        fetchFromFirstPage()
    }, [sortOrder.value])

    // The table's own toolbar has no Search button: its search (on Enter) and
    // its quality filters apply as soon as they change. They narrow the result
    // that is already on screen, so they amend the applied snapshot rather than
    // taking a new one — the filter bar may meanwhile hold a different site.
    useEffect(() => {
        if (!hasSearched.value || appliedFilters.value == null) return
        appliedFilters.value = {
            ...appliedFilters.value,
            text: searchText.value || '',
            flags: qualityFlags.value || {},
        }
        fetchFromFirstPage()
    }, [searchText.value, qualityFlags.value])

    return {
        isLoading: isLoadingTable.value,
        data: tableData.value,
        total: totalCount.value,
        refetch: fetchPage,
    }
}
